import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { FC } from 'react';
import { THEME } from '../../../../components-v2/Theme/theme';
import { ButtonBase } from '../../../../components/Button';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { LockIcon } from '../../../../components/svgs/icons/LockIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/Tooltip';

interface TagLockToggleProps {
	tag: Api.IAccountTag;
	onToggle: (isLocked: boolean) => void;
	isPending?: boolean;
	disabled?: boolean;
	disabledMessage?: string;
}

const styleSheet = StyleSheet.create({
	trigger: {
		alignItems: 'center',
		display: 'inline-flex',
		justifyContent: 'center',
		minHeight: 24,
		minWidth: 24,
	},
	triggerDisabled: {
		cursor: 'not-allowed',
		opacity: 0.5,
	},
	iconUnlocked: {
		opacity: 0.4,
	},
});

export const TagLockToggle: FC<TagLockToggleProps> = ({ tag, onToggle, isPending, disabled, disabledMessage }) => {
	const isLocked = !!tag.isLocked;
	const fillColor = isLocked ? THEME.colors.text.dark : THEME.colors.icon.gray;
	const baseLabel = isLocked ? 'Tag is locked. Click to unlock.' : 'Tag is unlocked. Click to lock.';
	const explanation =
		"When locked, only admins can add or remove this tag from contacts. Locking doesn't affect tags applied through integrations, automations, or surveys.";
	const tooltipText = disabled && disabledMessage ? disabledMessage : `${baseLabel} ${explanation}`;

	const handleClick = () => {
		if (disabled || isPending) return;
		onToggle(!isLocked);
	};

	const triggerContent = isPending ? (
		<LoadingSpinner type='tiny' />
	) : (
		<LockIcon fillColor={fillColor} className={!isLocked ? css(styleSheet.iconUnlocked) : undefined} />
	);

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<ButtonBase
					aria-label={baseLabel}
					aria-pressed={isLocked}
					disabled={disabled || isPending}
					onClick={handleClick}
					className={css(styleSheet.trigger, disabled && styleSheet.triggerDisabled)}
				>
					{triggerContent}
				</ButtonBase>
			</TooltipTrigger>
			<TooltipContent>{tooltipText}</TooltipContent>
		</Tooltip>
	);
};
